import React, { useCallback, useEffect, useState } from 'react';
import {
  GetServiceSettingByVendorId,
  CreateOrUpdateServiceSetting,
  GetAllVendorServices,
} from '../../../../../Services';
import {
  bottomBoxComponentUpdate,
  GetParams,
  GlobalHistory,
  showError,
  showSuccess,
} from '../../../../../Helper';
import { ButtonBase, Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import FieldsMapper from './components/FieldsMapper';
import Joi from 'joi';
import moment from 'moment';
import { Spinner, Tables } from '../../../../../Components';
import { TableActions } from '../../../../../Enums';
import ServiceManagementDialog from './components/EditServiceDialog';
import DeleteServiceDialog from './components/ServiceDeleteDialog';

const Services = ({ parentTranslationPath }) => {
  const { t } = useTranslation(parentTranslationPath);
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [filter, setFilter] = useState({ pageIndex: 1, pageSize: 10 });
  const [data, setData] = useState({ result: [], totalCount: 0 });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const vendorId = GetParams('id');

  const getServiceSettingByVendorId = async () => {
    try {
      setIsLoading(true);
      const res = await GetServiceSettingByVendorId(vendorId);
      const { createdBy, createdOn, ...rest } = res;
      rest.workingHoursFrom =
        rest.workingHoursFrom ||
        moment().subtract(8, 'hours').format('YYYY-MM-DD HH:mm');
      rest.workingHoursTo =
        rest.workingHoursTo || moment().format('YYYY-MM-DD HH:mm');
      setSettings(rest);
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setIsLoading(false);
    }
  };
  const getVendorServices = useCallback(async () => {
    try {
      const res = await GetAllVendorServices(vendorId, filter);
      setData(res);
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setIsLoading(false);
    }
  }, [filter]);
  const cancelHandler = () => {
    GlobalHistory.goBack();
  };

  const schema = Joi.object({
    citiesCovered: Joi.array()
      .when('specificAreaCovered', {
        is: true,
        then: Joi.array().required().min(1),
        otherwise: Joi.array().optional(),
      })
      .messages({
        'any.required': t(`field_is_required`, {
          field: t('citiesCovered'),
          ns: 'Agents',
        }),
        'array.min': t(`field_is_required`, {
          field: t('citiesCovered'),
          ns: 'Agents',
        }),
      }),
    daysOfOperation: Joi.array()
      .min(1)
      .required()
      .messages({
        'any.required': t(`field_is_required`, {
          field: t('daysOfOperation'),
          ns: 'Agents',
        }),
        'array.min': t(`field_is_required`, {
          field: t('daysOfOperation'),
          ns: 'Agents',
        }),
      }),
    workingHoursFrom: Joi.string().required(),
    workingHoursTo: Joi.string()
      .required()
      .custom((value, helpers) => {
        const dataTo = moment(value, 'YYYY-MM-DD HH:mm');
        const dataFrom = moment(settings.workingHoursFrom, 'YYYY-MM-DD HH:mm');
        if (dataTo.diff(dataFrom) < 0) {
          return helpers.error('state.dateRequired');
        }
      })
      .messages({
        'state.dateRequired': t(`working_hours_invalid`),
      }),
  })
    .options({ abortEarly: false, allowUnknown: true })
    .validate(settings);
  const saveHandler = useCallback(async () => {
    setIsSubmitted(true);
    try {
      if (schema.error) {
        showError(t('Shared:please-fix-all-errors'));
        return;
      }
      setIsLoading(true);
      await CreateOrUpdateServiceSetting({ ...settings, vendorId });
      showSuccess(t('settings_updated_successfully'));
      getServiceSettingByVendorId()
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setIsLoading(false);
    }
  }, [settings, schema.error]);
  const setDataHandler = ({ key, value }) => {
    setSettings((prevData) => ({ ...prevData, [key]: value }));
  };

  const onActionClicked = (actionEnum, item) => {
    setActiveItem(item);
    if (actionEnum === TableActions.edit.key) {
      setEditDialogOpen(true);
    } else if (actionEnum === TableActions.delete.key) {
      setDeleteDialogOpen(true);
    }
  };

  const onPageIndexChanged = (pageIndex) => {
    setFilter((item) => ({ ...item, pageIndex }));
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter((item) => ({ ...item, pageSize }));
  };

  useEffect(() => {
    getServiceSettingByVendorId();
  }, []);

  useEffect(() => {
    getVendorServices();
  }, [getVendorServices]);

  useEffect(() => {
    bottomBoxComponentUpdate(
      <div className='d-flex-v-center-h-end flex-wrap'>
        <ButtonBase
          className='btns theme-transparent mb-2'
          onClick={cancelHandler}
        >
          <span>{t('Shared:cancel')}</span>
        </ButtonBase>

        <ButtonBase
          className='btns theme-solid mb-2'
          onClick={saveHandler}
          disabled={isLoading}
        >
          <span>{t('Shared:save')}</span>
        </ButtonBase>
      </div>
    );
    return () => bottomBoxComponentUpdate(null);
  }, [saveHandler]);

  if (isLoading) {
    return <Spinner isAbsolute isActive />;
  }

  return (
    <div className='mt-2'>
      <FieldsMapper
        data={settings}
        setData={setDataHandler}
        parentTranslationPath={parentTranslationPath}
        schema={schema}
        isSubmitted={isSubmitted}
      />
      <div className='w-100 flex fj-end pt-5 pb-2 px-2 '>
        <ButtonBase
          className='btns theme-solid'
          onClick={() => {
            if (!settings.serviceSettingId) {
              return showError(t('setup_settings_first'));
            }
            setEditDialogOpen(true);
          }}
        >
          <span className='mdi mdi-plus' />
          {t(`add_service`)}
        </ButtonBase>
      </div>
      <Tables
        data={data.result}
        totalItems={data.totalCount}
        headerData={[
          {
            label: 'service_type',
            input: 'serviceType',
          },
          {
            label: 'service_availability',
            input: 'serviceAvailabilityName',
          },
          {
            label: 'service_category',
            input: 'vendorServiceCategoryName',
          },
          { label: 'description', input: 'description' },
          {
            label: 'tags',
            input: 'tags',
            component: (item) =>
              item.vendorServiceTags.map((tag) => (
                <Chip key={tag.tagId} label={tag.tagName} />
              )),
          },
        ]}
        onChangePage={onPageIndexChanged}
        onChangeRowsPerPage={onPageSizeChanged}
        itemsPerPage={filter.pageSize}
        activePage={filter.pageIndex - 1}
        defaultActions={[
          { enum: TableActions.edit.key },
          { enum: TableActions.delete.key },
        ]}
        actionsOptions={{ onActionClicked }}
        isOriginalPagination
        parentTranslationPath={parentTranslationPath}
      />
      {editDialogOpen && (
        <ServiceManagementDialog
          open
          parentTranslationPath={parentTranslationPath}
          onClose={() => setEditDialogOpen(false)}
          activeItem={activeItem}
          reload={getVendorServices}
          serviceSettingId={settings.serviceSettingId}
        />
      )}
      {deleteDialogOpen && (
        <DeleteServiceDialog
          activeItem={activeItem}
          open
          onClose={() => setDeleteDialogOpen(false)}
          onSave={getVendorServices}
        />
      )}
    </div>
  );
};
export default Services;
